//FUNCTIONS
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Loading from '../../components/Loading';
import { StoreState } from '../../store/createStore';
import {
  openShowModal,
  setIsCategory,
  setServiceInfo,
  viewModal,
} from '../../store/modules/parameter/actions';
import { findProduct } from '../../store/modules/product/actions';
import { useLocation } from 'react-router-dom';

//HOOKS
import { useCheckoutFunction } from '../../hooks/useCheckoutFunctions';
import { useTranslation } from '../../hooks/useTranslation';

//IMPORT TYPES
import { checkoutActionTypes } from '../../store/modules/checkout/types';
import { ServicesProps } from '../../store/modules/product/types';

//COMPONENTS
import Skeleton from 'react-loading-skeleton';
import Warranty from '../../components/Warranty';
import EspecificationItem from './EspecificationItem';

//STYLES
import ServiceInfoModal from '../../components/ServiceInfoModal';
import {
  ContainerBox,
  ContainerPrice,
  ContainerProduct,
  ContainerProductActions,
  ContainerProductDetails,
  ContainerProductFeatured,
  ContainerProductImage,
  ContainerProductImagePreview,
  ContainerProductImageSlug,
  ContainerProductStock,
  ContainerProductTitle,
  ContainerWarranty,
  ContentEspecificationItem,
  DetailsSKU,
  ProductImageSlugItem,
  Root,
  ButtonsContainer,
  MessageProductNotFound,
} from './styles';
import { formatQty } from '../../utils/formatyQtd';
import { getQuantityIncrement } from '../../utils/getQuantityIncrement';
import Button from '../../components/Button';
import { RoundHelper } from '../../utils/roundHelper';

// Função para formatar a unidade baseada na quantidade
function formatUnit(qty: number, un: string | undefined): string {
  if (un?.toUpperCase() === 'KG') {
    if (qty >= 1) {
      return `${qty}kg`;
    } else {
      return `${(qty * 1000).toFixed(0)}g`;
    }
  } else {
    return `${qty.toFixed(0)} ${un?.toLowerCase()}`;
  }
}

function decodeHtml(html: string) {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

const ProductView: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const productId = queryParams.get('id');
  const [itemHasCheckout, setItemHasCheckout] = useState(false);
  const [imageSelected, setImageSelected] = useState(0);
  const { handleCheckoutActions } = useCheckoutFunction();
  const dispatch = useDispatch();
  const { product, loadingProduct } = useSelector(
    (state: StoreState) => state.product,
  );
  const { multistoreSelected } = useSelector(
    (state: StoreState) => state.parameter,
  );
  const { checkout, loading: loadingCheckout } = useSelector((state: StoreState) => state.checkout);
  const { onTranslation: t } = useTranslation();
  const [warrantySelected, setWarrantySelected] = useState<ServicesProps>({
    service_id: -1,
    service_period: 0,
    service_price: '',
  } as ServicesProps);
  const [quantity, setQuantity] = useState('0,100');
  const [observation, setObservation] = useState('');
  const increment = getQuantityIncrement(product);

  useEffect(() => {
    dispatch(setIsCategory(false));
  }, [dispatch, product]);

  useEffect(() => {
    if (product.services && checkout.items) {
      if (checkout.items[product.product_id as string]) {
        setItemHasCheckout(true);
      }
    }
  }, [product, checkout]);

  useEffect(() => {
    const setOberservationItem = (observation?: string) => {
      if (observation && observation.length > 0) {
        setObservation(observation)
      }
    }
    if (product.un === 'Kg') {
      // Se o produto já está no carrinho, mostra a quantidade do carrinho
      const itemCarrinho = checkout.items[product.product_id as string];
      if (itemCarrinho) {
        setQuantity(Number(itemCarrinho.quantity).toFixed(3).replace('.', ','));
        setOberservationItem(itemCarrinho.additional.observation);
      } else {
        setQuantity('0,000');
      }
    } else {
      const itemCarrinho = checkout.items[product.product_id as string];
      if (itemCarrinho) {
        setQuantity(String(Number(itemCarrinho.quantity).toFixed(0)));
        setOberservationItem(itemCarrinho.additional.observation);
      } else {
        setQuantity('0');
      }
    }
  }, [product, checkout]);

  const handledSetWarrantySelected = useCallback(
    async (service: ServicesProps) => {
      setWarrantySelected(service);
    },
    [],
  );

  useEffect(() => {
    if (!product.id) {
      dispatch(findProduct({ id: productId as string }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
  ]);

  useEffect(() => {
    if (!loadingProduct) {
      dispatch(findProduct({ id: productId as string }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    multistoreSelected
  ]);

  const handledSetViewImage = useCallback(
    (id: number) => {
      setImageSelected(id);
    },
    [setImageSelected],
  );

  const handledSetWarrantyViewInfoModal = useCallback(
    (service: ServicesProps) => {
      dispatch(setServiceInfo(service));
      dispatch(openShowModal(true));
      dispatch(viewModal(ServiceInfoModal, { width: '50%', height: '80%' }));
    },
    [dispatch],
  );

  const isKg = product.un === 'Kg';

  const parseQty = (val: string) => {
    return val.replace('Kg', '').replace('kg', '').replace(',', '.').trim();
  };

  const handleProductCard = () => {
    const newQty = isKg
      ? parseFloat(quantity.replace(',', '.')).toFixed(3)
      : String(
        Math.floor(parseFloat(quantity.replace(',', '.'))),
      );
    if (product.product_id) {
      if (
        (isKg && newQty === "0.000")
        || (!isKg && newQty === "0")
      ) {
        handleCheckoutActions(
          checkoutActionTypes.REMOVE_PLU_CHECKOUT,
          product.product_id,
          '0'
        );
      } else {
        const itemCarrinho = checkout.items[product.product_id as string];
        if (!itemCarrinho) {
          handleCheckoutActions(
            checkoutActionTypes.ADD_PLU_CHECKOUT,
            product.product_id,
            newQty,
            undefined,
            undefined,
            observation,
          );
        } else {
          handleCheckoutActions(
            checkoutActionTypes.UPDATE_PLU_QTY_CHECKOUT,
            product.product_id,
            newQty,
            undefined,
            undefined,
            observation,
          );
        }
      }
    }
  }

  const handleQtyMinus = () => {
    if (loadingCheckout) {
      return;
    }
    let baseQty = parseFloat(parseQty(quantity));
    let newQty;
    if (isKg) {
      newQty = Math.max(0.0, Number((baseQty - increment).toFixed(3)));
      newQty = Math.round(newQty * 10) / 10;
    } else {
      newQty = Math.max(0, Math.floor(baseQty - increment));
    }

    setQuantity(isKg ? newQty.toFixed(3).replace('.', ',') : String(newQty));
  };
  const handleQtyPlus = () => {
    if (loadingCheckout) {
      return;
    }
    let baseQty = parseFloat(parseQty(quantity));
    let newQty;
    if (isKg) {
      newQty = Number((baseQty + increment).toFixed(3));
      //newQty = Math.round(newQty * 10) / 10;
    } else {
      newQty = Math.floor(baseQty + increment);
    }
    setQuantity(isKg ? newQty.toFixed(3).replace('.', ',') : String(newQty));
  };

  if (!product.id && !loadingProduct) {
    return (
      <Root>
        <MessageProductNotFound>Produto indisponível na loja selecionada</MessageProductNotFound>
      </Root>
    );
  }

  return (
    <Root>
      <ContainerBox>
        <ContainerProductImage>
          <ContainerProductImageSlug>
            {product.gallery_images &&
              product.gallery_images.map((image, idx) => (
                <Fragment key={image.large_image_url}>
                  <ProductImageSlugItem
                    bSelected={idx === imageSelected ? true : false}
                    onClick={() => handledSetViewImage(idx)}
                  >
                    {loadingProduct ? (
                      <Loading size={90} />
                    ) : (
                      <>
                        <img
                          src={image.small_image_url}
                          alt={product.short_description}
                          loading="lazy"
                        />
                      </>
                    )}
                  </ProductImageSlugItem>
                </Fragment>
              ))}
          </ContainerProductImageSlug>

          <ContainerProductImagePreview>
            {loadingProduct ? (
              <div className="loadingStyle">
                <Loading size={200} />
              </div>
            ) : (
              <>
                {product.gallery_images && (
                  <img
                    id="img-default"
                    src={
                      product.gallery_images[imageSelected]
                        ?.large_image_url
                    }
                    alt={product.short_description}
                    loading="lazy"
                  />
                )}
              </>
            )}
          </ContainerProductImagePreview>
        </ContainerProductImage>
        <ContainerProduct>
          <ContainerProductDetails>
            <DetailsSKU>
              <span>
                <b>{t('main.product_view.sku')}</b> {product.sku}
              </span>
            </DetailsSKU>
          </ContainerProductDetails>
          <ContainerProductTitle>
            {loadingProduct ? (
              <span
                style={{
                  width: '100%',
                  maxWidth: 500,
                }}
              >
                <Skeleton
                  width="80vw"
                  duration={2}
                  style={{
                    height: 30,
                    maxWidth: 500,
                  }}
                />
              </span>
            ) : (
              <span id="title-product">{decodeHtml(String(product.short_description))}</span>
            )}
          </ContainerProductTitle>
          <ContainerProductStock>
            <span>{t('main.product_view.status_product')}</span>
          </ContainerProductStock>
          <ContainerProductActions>
            <ContainerPrice>
              {loadingProduct ? (
                <span
                  style={{
                    marginLeft: 50,
                  }}
                >
                  <Loading size={80} />
                </span>
              ) : (

                <>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div className="dynamic-price">
                          {RoundHelper.toFormatted((() => {
                            const qty = parseFloat(quantity.replace(',', '.'));
                            const effectiveQty = qty > 0 ? qty : increment;
                            return RoundHelper.toNumber(product.price || 0) * effectiveQty;
                          })())}<span className="unit">/{formatUnit(parseFloat(quantity.replace(',', '.')) > 0 ? parseFloat(quantity.replace(',', '.')) : increment, product.un)}</span>
                        </div>
                        <div className="reference-price">
                          {RoundHelper.toFormatted(product.price || 0)}/{product.un?.toLowerCase()}
                        </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginTop: 12,
                    }}
                  >
                    <button
                      id="button-action-decrement"
                      onClick={handleQtyMinus}
                      disabled={
                        (isKg && parseFloat(quantity.replace(',', '.')) <= 0) ||
                        (!isKg && parseInt(quantity) <= 0)
                      }
                      style={{
                        fontSize: 22,
                        width: 32,
                        height: 32,
                        borderRadius: 16,
                        border: 'none',
                        background: '#eee',
                        cursor: ((isKg && parseFloat(quantity.replace(',', '.')) <= 0) ||
                          (!isKg && parseInt(quantity) <= 0))
                          ? 'not-allowed'
                          : 'pointer',
                        color: ((isKg && parseFloat(quantity.replace(',', '.')) <= 0) ||
                          (!isKg && parseInt(quantity) <= 0))
                          ? '#888'
                          : 'var(--primary)'
                      }}
                    >
                      –
                    </button>

                    <input
                      id="input-qtd-product"
                      style={{ width: 70, textAlign: 'center', fontSize: 18 }}
                      value={
                        formatQty(quantity, product.un)
                      }
                      disabled
                      inputMode={isKg ? 'decimal' : 'numeric'}
                      placeholder={isKg ? '0,000 kg' : '0'}
                    />
                    <button
                      id="button-action-increment"
                      style={{
                        fontSize: 22,
                        width: 32,
                        height: 32,
                        borderRadius: 16,
                        border: 'none',
                        background: '#eee',
                        cursor: 'pointer',
                      }}
                      onClick={handleQtyPlus}
                    >
                      +
                    </button>
                  </div>
                </>
              )}
            </ContainerPrice>

            <ButtonsContainer>
              {/* <AddCart
                id="button-action-cart"
                bCard={itemHasCheckout}
                onClick={() => {
                  handleProductCard();
                  history.push(routesEndpoints.NEW_CHECKOUT);
                }}
              >
                <span>
                  {itemHasCheckout && product.services
                    ? t('main.product_view.remove_card')
                    : t('main.product_view.card')}
                </span>
              </AddCart> */}
              <Button
                button={{ type: 'button', id: "button-action-buy" }}
                onClick={() => {
                  handleProductCard();
                }}
                atributes={{
                  buttonType: 'primary',
                  borderColor: 'var(--primary)',
                  textColor: '#FFF',
                  background: 'var(--primary)',
                  textSize: '16px',
                  padding: '12px 24px',
                  disableWitdthfixed: true,
                  textColorHover: '#FFF',
                  borderColorHover: 'var(--primary)',
                }}
              >
                <span>{t('main.product_view.buy')}</span>
              </Button>
              {/* <FavoriteCart>
                  <FaRegHeart size={24} />
                </FavoriteCart> */}
            </ButtonsContainer>
          </ContainerProductActions>


          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: '570px',
              minHeight: '120px',
              borderRadius: '20px',
              padding: '16px 24px',
              marginTop: '24px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              backgroundColor: '#ffffff',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow =
                '0 6px 16px rgba(0, 0, 0, 0.15)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow =
                '0 4px 12px rgba(0, 0, 0, 0.1)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <label
              htmlFor="observation"
              style={{
                display: 'block',
                marginBottom: '12px',
                fontWeight: 'bold',
                color: '#333',
                fontSize: '16px',
              }}
            >
              Adicionar Observação
            </label>
            <textarea
              id="observation"
              value={observation}
              onChange={e => setObservation(e.target.value)}
              placeholder="Ex: Corte fino / sem gordura"
              maxLength={500}
              style={{
                width: '100%',
                minHeight: '60px',
                padding: '12px',
                border: '2px solid #e8e8e8',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
                transition: 'border-color 0.3s ease',
                backgroundColor: '#fff',
                boxSizing: 'border-box',
              }}
              onFocus={e => {
                e.target.style.borderColor = '#c62d42';
              }}
              onBlur={e => {
                e.target.style.borderColor = '#e8e8e8';
              }}
            />
            {observation && (
              <small
                style={{
                  color: '#666',
                  fontSize: '12px',
                  marginTop: '8px',
                  display: 'block',
                }}
              >
                {observation.length}/500 caracteres
              </small>
            )}
          </div>

          {product.services && (
            <ContainerWarranty>
              <Warranty
                bReset={warrantySelected.service_id >= 0 ? false : true}
                setControlExtern
                itemHasCheckout={itemHasCheckout}
                services={product.services}
                handledSetWarrantySelectedExt={handledSetWarrantySelected}
                productPrice={product.price ? product.price : 0}
                productId={product.product_id ? product.product_id : ''}
                handledSetWarrantyViewInfoModal={
                  handledSetWarrantyViewInfoModal
                }
              />
            </ContainerWarranty>
          )}
        </ContainerProduct>
      </ContainerBox>
      <ContainerProductFeatured>
        <>
          <h1>{t('main.product_view.description')}</h1>
          <span>{decodeHtml(String(product.long_description))}</span>
        </>
        <>
          {product && product.attributes && (
            <ContentEspecificationItem>
              <h1>{t('main.product_view.especification')}</h1>
              <EspecificationItem
                especificationItem={product.attributes}
              />
            </ContentEspecificationItem>
          )}
        </>
      </ContainerProductFeatured>
    </Root>
  );
};

export default ProductView;
